import { useEffect, useState } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { UpliftModifierEnum, UpliftCapRateTypeEnum } from '../upliftCaps.types';
import { useGetUpliftIndexesForCaps } from '../hooks/useGetUpliftIndexesForCaps';
import UpliftCapSection from './UpliftCapSection';
import styles from './uplift-caps-form.module.scss';

export interface UpliftCapFormValues {
  modifier1: UpliftModifierEnum;
  endDate: Date | null;
  rate1Type: UpliftCapRateTypeEnum;
  rate1IndexId: string | null;
  rate1FixedValue: number | null;
  rate2Type: UpliftCapRateTypeEnum | null;
  rate2IndexId: string | null;
  rate2FixedValue: number | null;
  modifier2: UpliftModifierEnum;
  rate3Type: UpliftCapRateTypeEnum | null;
  rate3IndexId: string | null;
  rate3FixedValue: number | null;
  calculatedCapPercentage: number | null;
}

export interface UpliftCapsFormValues {
  existingUpliftCap: UpliftCapFormValues | null;
  furtherUpliftCap: UpliftCapFormValues | null;
}

const conditionalRateValidation = (
  ratePrefix: string,
  condField: string,
  condTest: (val: string) => boolean
) => ({
  [`${ratePrefix}Type`]: Yup.string().nullable(),
  [`${ratePrefix}IndexId`]: Yup.string()
    .nullable()
    .when([`${ratePrefix}Type`, condField], {
      is: (rt: string, cv: string) =>
        rt === UpliftCapRateTypeEnum.Index && condTest(cv),
      then: (schema) => schema.required(
        'Index is required when Rate Type is Index'
      ),
    }),
  [`${ratePrefix}FixedValue`]: Yup.number()
    .nullable()
    .when([`${ratePrefix}Type`, condField], {
      is: (rt: string, cv: string) =>
        rt === UpliftCapRateTypeEnum.Fixed && condTest(cv),
      then: (schema) => schema.required(
        'Fixed value is required when Rate Type is Fixed'
      ),
    }),
});

const isNotNone = (v: string) =>
  v !== UpliftModifierEnum.None;
const isRate2Visible = (v: string) =>
  v !== UpliftModifierEnum.None
  && v !== UpliftModifierEnum.Equals;
const isRate3Visible = (v: string) =>
  v === UpliftModifierEnum.MaximumOf;

const existingCapShape = Yup.object().shape({
  modifier1: Yup.string().required('Modifier 1 is required'),
  endDate: Yup.date().nullable().when('modifier1', {
    is: isNotNone,
    then: (schema) => schema.required(
      'End Date is required for Existing Uplift Cap'
    ),
  }),
  ...conditionalRateValidation('rate1', 'modifier1', isNotNone),
  ...conditionalRateValidation(
    'rate2', 'modifier1', isRate2Visible
  ),
  ...conditionalRateValidation(
    'rate3', 'modifier2', isRate3Visible
  ),
}).nullable();

const furtherCapShape = Yup.object().shape({
  modifier1: Yup.string().required('Modifier 1 is required'),
  endDate: Yup.date().nullable(),
  ...conditionalRateValidation('rate1', 'modifier1', isNotNone),
  ...conditionalRateValidation(
    'rate2', 'modifier1', isRate2Visible
  ),
  ...conditionalRateValidation(
    'rate3', 'modifier2', isRate3Visible
  ),
}).nullable();

const validationSchema = Yup.object().shape({
  existingUpliftCap: existingCapShape,
  furtherUpliftCap: furtherCapShape,
}).test(
  'further-end-date-not-before-existing',
  '',
  function (values) {
    const existingEnd = values?.existingUpliftCap?.endDate;
    const furtherEnd = values?.furtherUpliftCap?.endDate;
    if (!existingEnd || !furtherEnd) return true;
    if (new Date(furtherEnd) <= new Date(existingEnd)) {
      return this.createError({
        path: 'furtherUpliftCap.endDate',
        message:
          'End Date must be after Existing Uplift Cap End Date',
      });
    }
    return true;
  }
);

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface IUpliftCapsFormProps {
  initialValues: UpliftCapsFormValues;
  isLoading: boolean;
  isLoadingUpdate: boolean;
  isEditable: boolean;
  onSubmit: (values: UpliftCapsFormValues) => void;
  customerName: string;
  customerCode: string;
  contractNumber: string;
  startDate: string;
  endDate: string;
  totalValue: string;
  nextUpliftDate: string | null;
}

interface IFormFieldsProps {
  isDisabled: boolean;
  isEditable: boolean;
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  initialValues: UpliftCapsFormValues;
  customerName: string;
  customerCode: string;
  contractNumber: string;
  startDate: string;
  endDate: string;
  totalValue: string;
  nextUpliftDate: string | null;
}

function FormFields({
  isDisabled,
  isEditable,
  isDirty,
  setIsDirty,
  initialValues,
  customerName,
  customerCode,
  contractNumber,
  startDate,
  endDate,
  totalValue,
  nextUpliftDate,
}: IFormFieldsProps) {
  const { values, submitForm, resetForm, validateForm } =
    useFormikContext<UpliftCapsFormValues>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { indexOptions, isLoadingIndexes } = useGetUpliftIndexesForCaps();

  useEffect(() => {
    const hasChanges = JSON.stringify(values) !== JSON.stringify(initialValues);
    setIsDirty(hasChanges);
  }, [values, initialValues, setIsDirty]);

  const handleSaveClick = async () => {
    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      submitForm();
      return;
    }
    setShowConfirmationModal(true);
  };

  const handleConfirm = () => {
    setShowConfirmationModal(false);
    submitForm();
  };

  return (
    <Form>
      <AgConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        closeOnConfirm={false}
        onConfirm={handleConfirm}
        title={'You are about to modify the Uplift Cap settings for the Contract'}
      >
        You are about to modify the Uplift Cap settings for the Contract. <br />
        The updated settings will be visible to the users and might impact decision making on Uplifts.
        <br />
        Please confirm you wish to continue.
      </AgConfirmationModal>

      {/* Contract Summary Header */}
      <div className={styles.contractSummaryBar}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Contracted to</span>
          <span className={styles.summaryValue}>
            [{customerCode}] - {customerName}
          </span>
        </div>
        <div className={styles.summarySeparator} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Contract number</span>
          <span className={styles.summaryValue}>{contractNumber}</span>
        </div>
        <div className={styles.summarySeparator} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Start date</span>
          <span className={styles.summaryValue}>
            {formatDate(startDate)}
          </span>
        </div>
        <div className={styles.summarySeparator} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>End date</span>
          <span className={styles.summaryValue}>
            {formatDate(endDate)}
          </span>
        </div>
        <div className={styles.summarySeparator} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Total value</span>
          <span className={styles.summaryValue}>{totalValue}</span>
        </div>
        <div className={styles.summarySeparator} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Next uplift date</span>
          <span className={styles.summaryValue}>
            {formatDate(nextUpliftDate)}
          </span>
        </div>
      </div>

      {/* Uplift Cap Sections - side by side */}
      <div className={styles.sectionsRow}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Existing Uplift Cap</h3>
          </div>
          <UpliftCapSection
            prefix="existingUpliftCap"
            isEndDateRequired={true}
            isDisabled={isDisabled}
            isEditable={isEditable}
            indexOptions={indexOptions}
            isLoadingIndexes={isLoadingIndexes}
          />
        </div>

        {values.existingUpliftCap?.modifier1 !== UpliftModifierEnum.None && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Further Uplift Cap</h3>
            </div>
            <UpliftCapSection
              prefix="furtherUpliftCap"
              isEndDateRequired={false}
              isDisabled={isDisabled}
              isEditable={isEditable}
              indexOptions={indexOptions}
              isLoadingIndexes={isLoadingIndexes}
            />
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className={styles.submitRow}>
        <Button
          type="button"
          buttonStyleType={ButtonStyleType.PRIMARY}
          onClick={handleSaveClick}
          disabled={!isDirty || isDisabled}
        >
          Save
        </Button>
        <Button
          type="button"
          buttonStyleType={ButtonStyleType.SECONDARY}
          onClick={() => resetForm()}
          disabled={!isDirty}
        >
          Cancel
        </Button>
      </div>
    </Form>
  );
}

export default function UpliftCapsForm({
  initialValues,
  isLoading,
  isLoadingUpdate,
  isEditable,
  onSubmit,
  customerName,
  customerCode,
  contractNumber,
  startDate,
  endDate,
  totalValue,
  nextUpliftDate,
}: IUpliftCapsFormProps) {
  const [isDirty, setIsDirty] = useState(false);

  const isDisabled = !isEditable || isLoading || isLoadingUpdate;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={(formValues) => {
        onSubmit(formValues);
        setIsDirty(false);
      }}
    >
      <FormFields
        isDisabled={isDisabled}
        isEditable={isEditable}
        isDirty={isDirty}
        setIsDirty={setIsDirty}
        initialValues={initialValues}
        customerName={customerName}
        customerCode={customerCode}
        contractNumber={contractNumber}
        startDate={startDate}
        endDate={endDate}
        totalValue={totalValue}
        nextUpliftDate={nextUpliftDate}
      />
    </Formik>
  );
}
